import {PublicationContext} from 'centrifuge';
import {camelizeKeys} from 'humps';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {useCentrifugeSubscription} from '../../../provider/centrifuge/centrifuge-hook';
import {MainFilterContext} from '../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../provider/main-filter/main-filter-type';
import {
    ConcordanceAiPresetEnum,
    isReviewAnalysisSuggestAction,
} from '../review-analysis-ai-chat/review-analysis-ai-message-type';

import {
    AI_SUMMARY_INSUFFICIENT_STATUS,
    AI_SUMMARY_QUOTA_STATUS_CODE,
    requestReviewAnalysisAiSummary,
} from './review-analysis-ai-summary-api';

export enum AiSummaryStateEnum {
    Idle = 'idle',
    Loading = 'loading',
    Ready = 'ready',
    Insufficient = 'insufficient',
    QuotaExceeded = 'quota-exceeded',
    Error = 'error',
}

type OptionsType = {
    companyId?: number;
    aspectIds?: Array<number>;
    startDate: string | null;
    endDate: string | null;
    presetKey: ConcordanceAiPresetEnum;
};

function getCentrifugeChannel(channelId: string): string {
    return `public:rocket_gpt_${channelId}`;
}

export function useReviewAnalysisAiSummary(options: OptionsType): {
    summary: string;
    state: AiSummaryStateEnum;
    generate: () => void;
    isReady: boolean;
} {
    const {companyId, aspectIds, startDate, endDate, presetKey} = options;
    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);
    const {getSubscription} = useCentrifugeSubscription();

    const channelId = useMemo(() => uuidv4(), []);
    const [summary, setSummary] = useState('');
    const [state, setState] = useState<AiSummaryStateEnum>(AiSummaryStateEnum.Idle);

    useEffect(() => {
        function onPublication({data}: PublicationContext) {
            const camelizedData = camelizeKeys(data);

            if (!isReviewAnalysisSuggestAction(camelizedData)) {
                return;
            }

            if (camelizedData.stop) {
                setState(AiSummaryStateEnum.Ready);
                return;
            }

            setSummary((previous) => previous + camelizedData.text);
        }

        const subscription = getSubscription(getCentrifugeChannel(channelId));

        subscription?.on('publication', onPublication);

        return () => {
            subscription?.removeListener('publication', onPublication);
        };
    }, [channelId, getSubscription]);

    // Only gate on dates (seeded from the default range, so effectively always ready).
    // The filter key resolves asynchronously and is carried in via navigation — don't block
    // the button on it, or it can stay permanently disabled on a directly-opened page whose
    // key hasn't resolved. A missing key surfaces as a retryable error, not a dead button.
    const isReady = Boolean(startDate && endDate);

    const generate = useCallback(() => {
        if (!isReady) {
            return;
        }

        setSummary('');
        setState(AiSummaryStateEnum.Loading);

        requestReviewAnalysisAiSummary({
            channelId,
            requestId: uuidv4(),
            filterKey: mainFilterKey,
            presetKey,
            searchParams: {startDate, endDate, companyId, aspectIds},
        })
            .then((result) => {
                // 204 => streaming; keep Loading until Centrifugo sends `stop`.
                if (result?.status === AI_SUMMARY_INSUFFICIENT_STATUS) {
                    setState(AiSummaryStateEnum.Insufficient);
                }
            })
            .catch((error: unknown) => {
                if (
                    error !== null &&
                    typeof error === 'object' &&
                    'statusCode' in error &&
                    error.statusCode === AI_SUMMARY_QUOTA_STATUS_CODE
                ) {
                    setState(AiSummaryStateEnum.QuotaExceeded);
                } else {
                    setState(AiSummaryStateEnum.Error);
                }
            });
    }, [isReady, channelId, mainFilterKey, presetKey, startDate, endDate, companyId, aspectIds]);

    return {summary, state, generate, isReady};
}
